import type { CarAd } from "@mobility-item-page/types";
import { EvInfoSection } from "../../ev/EvInfoSection.tsx";
import { KeyInfo } from "../../key-info/KeyInfo.tsx";
import { AnnualTax } from "../AnnualTax.tsx";
import { ChassisNumber } from "../ChassisNumber.tsx";
import { ConditionReport } from "../ConditionReport.tsx";
import { Engine } from "../Engine.tsx";
import { EuCheck } from ".././EuCheck.tsx";
import { FirstRegistration } from "../FirstRegistration.tsx";
import { Fuel } from "../Fuel.tsx";
import { Make } from "../Make.tsx";
import { Mileage } from "../Mileage.tsx";
import { Model } from "../Model.tsx";
import { ModelYear } from "../ModelYear.tsx";
import { NoOfSeats } from "../NoOfSeats.tsx";
import { Owners } from "../Owners.tsx";
import { RegistrationNumber } from "../RegistrationNumber.tsx";
import { RegistrationTax } from "../RegistrationTax.tsx";
import { SpecificationsArea } from "../SpecificationsArea.tsx";
import { Transmission } from "../Transmission.tsx";
import { Warranty } from "../Warranty.tsx";
import { Weight } from "../Weight.tsx";
import { WheelDrive } from "../WheelDrive.tsx";
import { BatteryCapacity } from "./BatteryCapacity.tsx";
import { BodyType } from "./BodyType.tsx";
import { BootSize } from "./BootSize.tsx";
import { CarLocation } from "./CarLocation.tsx";
import { Co2Emissions } from "./Co2Emissions.tsx";
import { DrivingRange } from "./DrivingRange.tsx";
import { ExteriorColor } from "./ExteriorColor.tsx";
import { ExteriorColorDescription } from "./ExteriorColorDescription.tsx";
import { FuelConsumption } from "./FuelConsumption.tsx";
import { InteriorColor } from "./InteriorColor.tsx";
import { MaxTrailerWeight } from "./MaxTrailerWeight.tsx";
import { NoOfDoors } from "./NoOfDoors.tsx";
import { RegistrationClass } from "./RegistrationClass.tsx";
import { SalesForm } from "./SalesForm.tsx";
import { TransmissionSpec } from "./TransmissionSpec.tsx";
import { WheelDriveSpec } from "./WheelDriveSpec.tsx";

export const CarSpecifications = ({ ad }: { ad: CarAd }) => {
  const specifications = () => {
    switch (ad.adViewType) {
      case "car-used":
      case "car-new":
      case "car-used-business":
        return (
          <>
            <RegistrationTax {...(ad.price ?? {})} />
            <AnnualTax annualTaxUrl={ad.annual_tax_url} />
            <Specifications ad={ad} />
          </>
        );
      case "car-leasing-business":
      case "car-leasing":
        return <Specifications ad={ad} />;
      default:
        return null;
    }
  };

  const content = specifications();

  return (
    <SpecificationsArea>
      <EvInfoSection />
      {content && <KeyInfo>{content}</KeyInfo>}
    </SpecificationsArea>
  );
};

const Specifications = ({ ad }: { ad: CarAd }) => {
  return (
    <>
      {/*General information*/}
      <Make make={ad.model_and_make?.parent?.value} />
      <Model model={ad.model_and_make?.value} />
      <ModelYear year={ad.year} />
      <BodyType bodyType={ad.body_type} />

      {/*Product properties*/}
      {ad.engine && <Fuel fuel={ad.engine.fuel} />}
      <Engine engine={ad.engine} />
      <Co2Emissions co2Emissions={ad.co2_emissions} />
      <Mileage distance={ad.mileage} unit={ad.mileage_unit} />
      <BatteryCapacity batteryCapacity={ad.battery_capacity} />
      <DrivingRange drivingRange={ad.driving_range} />
      <FuelConsumption fuelConsumption={ad.fuel_consumption} />
      <Transmission transmission={ad.transmission} />
      <TransmissionSpec transmissionSpec={ad.transmission_spec} />
      <MaxTrailerWeight maxTrailerWeight={ad.max_trailer_weight} />
      <WheelDrive wheelDrive={ad.wheel_drive} />
      <WheelDriveSpec wheelDriveSpec={ad.wheel_drive_spec} />
      <Weight weight={ad.weight} />
      <NoOfSeats noOfSeats={ad.no_of_seats} />
      <NoOfDoors noOfDoors={ad.no_of_doors} />
      <BootSize bootSize={ad.boot_size} />

      {/*Appearance*/}
      <ExteriorColor exteriorColor={ad.exterior_color} />
      <ExteriorColorDescription exteriorColorDescription={ad.exterior_color_description} />
      <InteriorColor interiorColor={ad.interior_color} />

      {/*Registration, warranty, owners, etc*/}
      <CarLocation carLocation={ad.car_location} />
      {"eu_check" in ad && <EuCheck euCheck={ad.eu_check} />}
      <RegistrationClass registrationClass={ad.registration_class} />
      <RegistrationNumber registrationNumber={ad.registration_number} />
      <ChassisNumber chassisNumber={ad.chassis_number} />
      <FirstRegistration firstRegistration={ad.first_registration} />
      {"owners" in ad && <Owners owners={ad.owners} />}

      <Warranty warranty={ad.warranty} />
      {"condition_report" in ad && <ConditionReport conditionReport={ad.condition_report} />}
      <SalesForm salesForm={ad.sales_form} />
    </>
  );
};
